import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Message } from 'rsuite';
import { SortableTh, sortRows, useTableSort } from '@nsi-advisory-services/components';
import PageHeader from '../components/shared/PageHeader';
import { showToast } from '../utils/toast';
import {
  useBulkApprovePeriods,
  usePeriodAction,
  useTimesheetApprovals,
  useTimesheetConfig,
} from '../hooks/useTimesheet';
import {
  Metric,
  DateStepper,
  ProviderBanner,
  StatusPill,
  TimesheetNav,
} from '../components/timesheet/TimesheetParts';
import PeriodActionModal from '../components/timesheet/PeriodActionModal';
import {
  formatHours,
  formatPeriod,
  formatTimestamp,
  isValidIso,
  nextPeriod,
  periodFor,
  previousPeriod,
  todayIso,
} from '../utils/timesheet';
import './Timesheet.css';

const FILTERS = [
  { key: 'submitted', label: 'Awaiting review' },
  { key: 'all', label: 'Everyone' },
  { key: 'missing', label: 'Not submitted' },
  { key: 'returned', label: 'Returned' },
  { key: 'approved', label: 'Approved' },
];

// Early in a period, the one that needs reviewing is the one just finished.
const defaultPeriod = () => {
  const today = todayIso();
  const current = periodFor(today);
  const dayOfPeriod = Number(today.slice(8, 10)) - Number(current.start.slice(8, 10));
  return dayOfPeriod < 5 ? previousPeriod(current) : current;
};

/**
 * The review roster for a pay period: everyone the reviewer is responsible
 * for, where their timesheet stands, and the actions to move it on.
 */
export default function TimesheetApprovalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const startParam = searchParams.get('start');
  const period = isValidIso(startParam) ? periodFor(startParam) : defaultPeriod();
  const [filter, setFilter] = useState('submitted');
  const [selected, setSelected] = useState([]);
  const [returning, setReturning] = useState(null);
  const [actionError, setActionError] = useState(null);

  const configQuery = useTimesheetConfig();
  const config = configQuery.data;
  const approvalsQuery = useTimesheetApprovals({ start: period.start }, { enabled: config?.installed !== false });
  const periodAction = usePeriodAction();
  const bulkApprove = useBulkApprovePeriods();
  const sort = useTableSort('name');

  const rows = useMemo(() => approvalsQuery.data?.rows || [], [approvalsQuery.data]);
  const counts = useMemo(() => ({
    submitted: rows.filter((r) => r.status === 'submitted').length,
    approved: rows.filter((r) => r.status === 'approved').length,
    returned: rows.filter((r) => r.status === 'returned').length,
    missing: rows.filter((r) => r.status === 'open').length,
  }), [rows]);

  const filtered = useMemo(() => {
    const list = rows.filter((r) => {
      if (filter === 'all') return true;
      if (filter === 'missing') return r.status === 'open';
      return r.status === filter;
    });
    return sortRows(list, sort.sortKey, sort.sortDir, {
      name: (r) => r.name || '',
      status: (r) => r.status,
      hours: (r) => r.hours,
      billable: (r) => r.billableHours,
      days: (r) => r.daysLogged,
      submitted: (r) => r.submittedAt || '',
    });
  }, [rows, filter, sort.sortKey, sort.sortDir]);

  const submittedIds = filtered.filter((r) => r.status === 'submitted').map((r) => r.employeeId);
  const allSelected = submittedIds.length > 0 && submittedIds.every((id) => selected.includes(id));

  const changePeriod = (next) => {
    setSelected([]);
    const params = new URLSearchParams(searchParams);
    params.set('start', next.start);
    setSearchParams(params, { replace: true });
  };

  const approve = async (row) => {
    try {
      await periodAction.mutateAsync({ action: 'approve', employeeId: row.employeeId, start: period.start });
      showToast('success', `Approved ${row.name}'s timesheet.`);
      setSelected((prev) => prev.filter((id) => id !== row.employeeId));
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  const approveSelected = async () => {
    try {
      const result = await bulkApprove.mutateAsync({ start: period.start, employeeIds: selected });
      showToast(result.skipped?.length ? 'warning' : 'success',
        `Approved ${result.approved} timesheet${result.approved === 1 ? '' : 's'}${result.skipped?.length ? `; ${result.skipped.length} skipped` : ''}.`);
      setSelected([]);
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  const confirmReturn = async (comment) => {
    setActionError(null);
    try {
      await periodAction.mutateAsync({ action: 'return', employeeId: returning.employeeId, start: period.start, comment });
      showToast('success', `Returned ${returning.name}'s timesheet.`);
      setReturning(null);
    } catch (err) {
      setActionError(String(err?.message || err));
    }
  };

  const th = (key, label, className = '') => (
    <SortableTh sort={sort} sortKey={key} className={className || undefined}>
      {label}
    </SortableTh>
  );

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Timesheet', to: '/timesheet' }, { label: 'Approvals' }]}
        icon="bi-check2-square"
        title="Timesheet Approvals"
        actions={<TimesheetNav config={config} />}
        showBack={false}
      />

      {/* The same place and control as on My Timesheet, a pay period at a step. */}
      <div className="ts-toolbar">
        <DateStepper
          date={period.start}
          label={`Pay period, ${formatPeriod(period)}`}
          unit="pay period"
          onStep={(dir) => changePeriod(dir > 0 ? nextPeriod(period) : previousPeriod(period))}
          onJump={(iso) => changePeriod(periodFor(iso))}
          onToday={() => changePeriod(periodFor(todayIso()))}
          showToday={period.start !== periodFor(todayIso()).start}
          slideKey={period.start}
        />
      </div>

      <ProviderBanner config={config} />

      {approvalsQuery.error ? (
        <Message type="error" showIcon>{String(approvalsQuery.error?.message || approvalsQuery.error)}</Message>
      ) : null}

      <section className="bdc-metrics">
        <Metric label="Awaiting review" value={counts.submitted} foot={counts.submitted ? 'Submitted and locked' : 'Nothing waiting'} />
        <Metric label="Not submitted" value={counts.missing} foot={period.end < todayIso() && counts.missing ? 'The period has ended' : formatPeriod(period)} footTone={period.end < todayIso() && counts.missing ? 'bad' : null} />
        <Metric label="Returned" value={counts.returned} foot="Waiting on the employee" />
        <Metric label="Approved" value={counts.approved} foot={rows.length ? `of ${rows.length} people` : null} footTone={rows.length && counts.approved === rows.length ? 'good' : null} />
      </section>

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <div className="bdc-segment" role="group" aria-label="Filter">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`bdc-btn bdc-btn--sm${filter === f.key ? ' bdc-btn--primary' : ''}`}
                  aria-pressed={filter === f.key}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  {f.key !== 'all' && counts[f.key] ? <span className="bdc-count ms-1">{counts[f.key]}</span> : null}
                </button>
              ))}
            </div>
          </div>
          <div className="bdc-table-head__actions">
            <button
              type="button"
              className="bdc-btn bdc-btn--sm bdc-btn--primary"
              onClick={approveSelected}
              disabled={selected.length === 0 || bulkApprove.isPending}
            >
              <i className="bi bi-check2-all" aria-hidden="true" />
              Approve selected{selected.length ? ` (${selected.length})` : ''}
            </button>
          </div>
        </div>
        <table className="nsi-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  aria-label="Select all submitted"
                  checked={allSelected}
                  disabled={submittedIds.length === 0}
                  onChange={(e) => setSelected(e.target.checked ? submittedIds : [])}
                />
              </th>
              {th('name', 'Employee')}
              {th('status', 'Status')}
              {th('hours', 'Hours', 'nsi-table__right')}
              {th('billable', 'Billable', 'nsi-table__right')}
              {th('days', 'Days', 'nsi-table__right')}
              {th('submitted', 'Submitted')}
              <th>Review</th>
              <th className="nsi-table__right"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {approvalsQuery.isLoading ? (
              <tr><td colSpan={9} className="nsi-table__empty">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="nsi-table__empty">
                  {filter === 'submitted' ? 'Nothing is waiting for review in this period.' : 'Nobody matches this filter.'}
                </td>
              </tr>
            ) : filtered.map((row) => (
              <tr key={row.employeeId}>
                <td>
                  {row.status === 'submitted' ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.name}`}
                      checked={selected.includes(row.employeeId)}
                      onChange={(e) => setSelected((prev) => (e.target.checked
                        ? [...prev, row.employeeId]
                        : prev.filter((id) => id !== row.employeeId)))}
                    />
                  ) : null}
                </td>
                <td>
                  <Link to={`/timesheet?employee=${row.employeeId}&date=${period.start}&view=period`}>{row.name}</Link>
                </td>
                <td><StatusPill status={row.status} /></td>
                <td className="nsi-table__right bdc-num fw-semibold">{formatHours(row.hours)}</td>
                <td className="nsi-table__right bdc-num">{formatHours(row.billableHours)}</td>
                <td className="nsi-table__right bdc-num">{row.daysLogged}</td>
                <td className="nsi-table__muted">{row.submittedAt ? formatTimestamp(row.submittedAt) : '—'}</td>
                <td className="bdc-table-wrap nsi-table__muted">
                  {row.reviewedAt ? `${row.reviewedBy || 'Reviewer'} · ${formatTimestamp(row.reviewedAt)}` : '—'}
                  {row.reviewComment ? <div className="small">“{row.reviewComment}”</div> : null}
                </td>
                <td className="nsi-table__right text-nowrap">
                  <Link className="bdc-linkbtn text-decoration-none" to={`/timesheet?employee=${row.employeeId}&date=${period.start}&view=period`}>Open</Link>
                  {row.status === 'submitted' ? (
                    <>
                      <button type="button" className="bdc-linkbtn" onClick={() => approve(row)} disabled={periodAction.isPending}>Approve</button>
                      <button type="button" className="bdc-linkbtn" onClick={() => setReturning(row)}>Return</button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PeriodActionModal
        open={Boolean(returning)}
        onClose={() => {
          setReturning(null);
          setActionError(null);
        }}
        onConfirm={confirmReturn}
        title="Return Timesheet"
        confirmLabel="Return"
        confirmColor="orange"
        commentLabel="What needs to change?"
        commentRequired
        loading={periodAction.isPending}
        error={actionError}
      >
        <p className="mb-0">
          {returning?.name}’s timesheet for {formatPeriod(period)} goes back to them to fix and resubmit.
        </p>
      </PeriodActionModal>
    </div>
  );
}
